import {
  type CellContext,
  type ColumnDef,
  type ColumnDefTemplate,
  type RowData as TanStackRowData,
  type SortDirection,
  type SortingState,
  flexRender,
  useTable,
} from '@tanstack/react-table';
import { ArrowRight, ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { PageNavigation } from '@/shared/components/page-navigation/page-navigation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { formatNumber } from '@/shared/utils/format-number';
import { cn } from '../../utils/cn';
import { Stage } from '../stage/stage';
import { TableSkeleton } from '../table-skeleton/table-skeleton';
import { creatorTableFeatures, type CreatorTableFeatures } from './features';

export type CellRenderer = 'text' | 'number' | 'custom';

export interface TableColumnConfig<TData extends TanStackRowData> {
  key: keyof TData | '__index__';
  header: string | ReactNode;
  cell: ColumnDefTemplate<CellContext<CreatorTableFeatures, TData, any>>;
  size: string;
  maxSize?: string;
  enableSorting?: boolean;
  accessorFn?: (row: TData) => unknown;
  tableCellClassName?: string;
}

interface DataTableProps<TData extends TanStackRowData> {
  data: TData[];
  header: ReactNode;
  onRowClick?: (data: TData, event: React.MouseEvent) => void;
  showIndexColumn?: boolean;
  columns: TableColumnConfig<TData>[];
  pageSize?: number;
  minHeight?: string;
  enablePagination?: boolean;
  isLoading?: boolean;
}

export function DataTable<TData extends TanStackRowData>({
  data,
  header,
  onRowClick,
  columns,
  pageSize = 5,
  minHeight,
  enablePagination = true,
  showIndexColumn = true,
  isLoading = false,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const _columns = useMemo(() => {
    return showIndexColumn ? [getRowIndexColumn<TData>(), ...columns] : columns;
  }, [columns, showIndexColumn]);

  const gridTemplateColumns = useMemo(() => {
    return _columns.map((col) => {
      return col.maxSize != null ? `minmax(${col.size}, ${col.maxSize})` : `${col.size}`;
    });
  }, [_columns]);

  const tableColumns = useMemo<ColumnDef<CreatorTableFeatures, TData, any>[]>(
    () =>
      _columns.map((col) => {
        return {
          accessorKey: String(col.key),
          header: typeof col.header === 'string' ? col.header : () => col.header,
          enableSorting: col.enableSorting ?? false,
          cell: col.cell,
          meta: {
            tableCellClassName: col.tableCellClassName,
          },
        };
      }),
    [_columns],
  );

  const table = useTable({
    features: creatorTableFeatures,
    data,
    columns: tableColumns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
  });

  const showPagination = enablePagination && data.length > pageSize;

  const skeletonColumns = useMemo(() => {
    return gridTemplateColumns.map((column) => {
      if (column.includes('minmax')) {
        const size = parseInt(column.split('minmax(')[1].split(')')[0]);
        return { className: `w-[${size}px]` };
      }
      if (column.includes('fr')) {
        return { className: 'w-full' };
      }
      return { className: `w-[${column}]` };
    });
  }, [gridTemplateColumns]);

  return (
    <div className="h-full rounded-lg border bg-white shadow-sm">
      <div className="p-6">
        <div className="mb-6">{header}</div>

        <div style={{ minHeight: minHeight ?? `${pageSize * 50 + 20}px` }}>
          <Stage>
            <Stage.Scene status={isLoading}>
              <TableSkeleton columns={skeletonColumns} rows={pageSize} rowHeight="h-[72px]" />
            </Stage.Scene>
            <Stage.Scene default>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={headerGroup.id}
                      gridTemplateColumns={gridTemplateColumns.join(' ')}
                    >
                      {headerGroup.headers.map((header) => {
                        const headerNode = flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        );
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder ? null : header.column.columnDef.enableSorting ? (
                              <SortableHeader
                                currentSort={header.column.getIsSorted()}
                                onSortToggle={() => {
                                  const currentSort = header.column.getIsSorted();
                                  switch (currentSort) {
                                    case 'asc':
                                      header.column.toggleSorting(true);
                                      break;
                                    case 'desc':
                                      header.column.toggleSorting(false);
                                      break;
                                    default:
                                      header.column.toggleSorting(true);
                                      break;
                                  }
                                }}
                              >
                                {headerNode}
                              </SortableHeader>
                            ) : (
                              headerNode
                            )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      gridTemplateColumns={gridTemplateColumns.join(' ')}
                      className={onRowClick ? 'cursor-pointer hover:bg-zinc-50' : ''}
                      onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const tableCellClassName = cell.column.columnDef.meta?.tableCellClassName;
                        return (
                          <TableCell key={cell.id} className={tableCellClassName}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stage.Scene>
          </Stage>
        </div>
      </div>
      {showPagination && <PageNavigation className="border-base-border border-t" table={table} />}
    </div>
  );
}

DataTable.Cell = function Cell({
  getValue,
  className,
}: {
  getValue: () => string | number;
  className?: string;
}) {
  const value = getValue();

  // Handle undefined/null values safely
  if (value === undefined || value === null) {
    return (
      <span className={cn('text-base-foreground block truncate font-normal', className)} title="">
        -
      </span>
    );
  }

  return (
    <span
      className={cn('text-base-foreground block truncate font-normal', className)}
      title={value.toString()}
    >
      {typeof value === 'number' ? formatNumber(value) : value}
    </span>
  );
};

DataTable.Header = function TableHeader({
  title,
  subtitle,
  onRightClick,
}: {
  title: string;
  subtitle?: string;
  onRightClick?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-zinc-950">{title}</h3>
          {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      {onRightClick != null && (
        <button
          className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-zinc-100"
          onClick={onRightClick}
        >
          <ArrowRight className="text-base-foreground h-4 w-4" />
        </button>
      )}
    </div>
  );
};

function getRowIndexColumn<TData extends TanStackRowData>(): TableColumnConfig<TData> {
  return {
    // `ROW_INDEX_COLUMN` is a special column that is used to display the index of the row.
    // So we need to use `__index__` as the accessor key by forcing the type to `keyof RowData`.
    key: '__index__' as keyof TData,
    header: '#',
    size: '60px',
    cell: ({ row, table }) => {
      const pageIndex = table.store.state.pagination.pageIndex;
      const pageSize = table.store.state.pagination.pageSize;
      const rowModel = table.getRowModel();
      const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
      const actualIndex = pageIndex * pageSize + visualIndex + 1;
      return <span className="text-base-foreground font-normal">{actualIndex}</span>;
    },
    enableSorting: false,
  };
}

function SortableHeader({
  children,
  onSortToggle,
  currentSort,
}: {
  children: ReactNode;
  onSortToggle?: () => void;
  currentSort?: SortDirection | false;
}) {
  return (
    <div className="flex items-center">
      {children}
      <button
        className="ml-1 inline-flex h-4 w-4 items-center justify-center"
        onClick={onSortToggle}
      >
        {!currentSort ? (
          <ChevronsUpDownIcon className="h-3 w-3 text-zinc-500" />
        ) : currentSort === 'asc' ? (
          <ChevronUpIcon className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 text-zinc-500" />
        )}
      </button>
    </div>
  );
}
